import React, { useEffect, useState, useCallback } from 'react';
import Banner from '../../components/Banner/banner';
import SEO from '../../components/SEO/SEO';
import imgBanner from '../../assets/img/slider-pagina-book.png';
import { useTranslation } from 'react-i18next';
import './gallery.scss';

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [t] = useTranslation('global');

  useEffect(() => {
    const importAll = (r) => r.keys().map((item) => r(item));
    try {
      const bookImages = importAll(
        require.context('../../assets/book', false, /\.(png|jpe?g|svg)$/)
      );
      setImages(bookImages);
    } catch (err) {
      console.error('Error loading gallery images:', err);
    }
  }, []);

  const openModal = (index) => {
    setSelectedIndex(index);
  };

  const closeModal = () => {
    setSelectedIndex(null);
  };

  const changeSlide = useCallback(
    (direction) => {
      if (selectedIndex === null || images.length === 0) return;
      let nextIndex = selectedIndex + direction;
      if (nextIndex >= images.length) {
        nextIndex = 0;
      } else if (nextIndex < 0) {
        nextIndex = images.length - 1;
      }
      setSelectedIndex(nextIndex);
    },
    [selectedIndex, images.length]
  );

  // Keyboard navigation for Lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIndex === null) return;
      if (e.key === 'Escape') {
        closeModal();
      } else if (e.key === 'ArrowRight') {
        changeSlide(1);
      } else if (e.key === 'ArrowLeft') {
        changeSlide(-1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, changeSlide]);

  return (
    <main className="globalContainer">
      <SEO title={t('Nav.Gallery')} canonicalPath="/gallery" />

      <Banner name={imgBanner} alt="Laura Candela Photo Book" />

      <section className="globalSection" aria-label="Photo Gallery">
        <h1 className="sectionTitle">{t('GalleryTitle')}</h1>

        {/* Thumbnail Grid */}
        <div className="picContainer">
          {images.map((imgSrc, index) => (
            <button
              key={index}
              type="button"
              className="photoThumbnailBtn"
              onClick={() => openModal(index)}
              aria-label={`Open photo ${index + 1} in lightbox`}
            >
              <img
                className="photosBook"
                src={imgSrc}
                alt={`Laura Candela Book portrait ${index + 1}`}
                loading="lazy"
              />
            </button>
          ))}
        </div>

        {/* Lightbox Modal */}
        {selectedIndex !== null && (
          <div
            className="bigPicContainer"
            role="dialog"
            aria-modal="true"
            aria-label="Photo Lightbox"
            onClick={closeModal}
          >
            {/* Prev Button */}
            <button
              type="button"
              className="picActions pointer prev"
              onClick={(e) => {
                e.stopPropagation();
                changeSlide(-1);
              }}
              aria-label="Previous photo"
            >
              &#10094;
            </button>

            {/* Next Button */}
            <button
              type="button"
              className="picActions pointer next"
              onClick={(e) => {
                e.stopPropagation();
                changeSlide(1);
              }}
              aria-label="Next photo"
            >
              &#10095;
            </button>

            {/* Close Button */}
            <button
              type="button"
              className="picActions close"
              onClick={(e) => {
                e.stopPropagation();
                closeModal();
              }}
              aria-label="Close photo lightbox"
            >
              &times;
            </button>

            {/* Image in Modal */}
            <div
              className="modalImageWrapper"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                className="bigPic"
                src={images[selectedIndex]}
                alt={`Laura Candela full portrait ${selectedIndex + 1}`}
              />
              <span className="photoCounter">
                {selectedIndex + 1} / {images.length}
              </span>
            </div>
          </div>
        )}
      </section>
    </main>
  );
};

export default Gallery;